import type { EnvironmentProviders } from '@angular/core';
import { effect, inject, provideEnvironmentInitializer } from '@angular/core';
import { UiDevModeStore } from '@core/dev-mode/ui-dev-mode.store';

export const UiDevModeSessionStorageKey = 'dx_ui_dev_mode';

/**
 * Keeps the UI Dev Mode flags across reloads of this tab.
 *
 * Session storage rather than local storage, like the DX opt-in itself: a flag set while
 * debugging must not follow the developer into tomorrow's demo. Wired only from
 * `dx.providers.ts`, so production never reads a flag someone planted in storage.
 *
 * @returns {EnvironmentProviders} The initializer restoring and persisting the flags.
 */
export function provideUiDevModePersistence(): EnvironmentProviders {
  return provideEnvironmentInitializer(() => {
    const store = inject(UiDevModeStore);

    try {
      const persisted = sessionStorage.getItem(UiDevModeSessionStorageKey);
      if (persisted) {
        store.restore(JSON.parse(persisted) as Record<string, unknown>);
      }
    } catch {
      // unreadable storage or a corrupt value: start with every flag off
    }

    effect(() => {
      const flags = store.flags();
      try {
        if (store.enabledCount() === 0) {
          sessionStorage.removeItem(UiDevModeSessionStorageKey);
        } else {
          sessionStorage.setItem(UiDevModeSessionStorageKey, JSON.stringify(flags));
        }
      } catch {
        // storage unavailable: the flags still work for this page load
      }
    });
  });
}
